import type { AfterCellChanged, Rows } from 'common/types/scalarSpreadsheet/scalarSpreadsheet';
import { AllocationWeightedShareValue, ValuationAllocation } from 'common/types/valuation';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import type { ConfigurationCellMap } from 'models/interfaces/spreadsheet-types/core-types';
import generateEquityAllocationKey from 'pages/ValuationsAllocation/allocation/EquityAllocation/config/util/generate-equity-allocation-key';
import { AllocationScenarioDetailSharesSecurityModel } from 'pages/ValuationsAllocation/util/getCapTableSecurities/types';
import { createColumns } from './createColumns';
import type { Columns } from './createColumns/types';
import { customParser } from './customParser';
import { customRowConfig } from './customRowConfig';
import { RANGE_SENSITIVITY_COLUMN_HEADERS } from '../constants';

/**
 * SpreadsheetConfig is generic as `SpreadsheetConfig<I, COLUMN, TD, CD = object>` (see SpreadsheetConfig.ts).
 * TypeScript requires three or four type arguments; we pass three so `CD` stays the default `object`.
 * We use `object` for `I`, `COLUMN`, and `TD` because this sheet does not thread a single strongly typed
 * domain model through parser/reverseParser/workbook the way some valuation sheets do—narrower types would
 * force casts at every boundary without improving safety here.
 */
type RangeSensitivitySpreadsheetConfig = SpreadsheetConfig<object, object, object>;

export interface CreateRangeSensitivityConfigurationParams {
  /** Unique workbook key (must match config `spreadsheets` object key). */
  workbookKey: string;
  /** Workbook key of the companion sensitivity-% input sheet (used in cross-sheet bound expressions). */
  pctSheetKey: string;
  allocation: ValuationAllocation;
  companyName: string;
  /** Company id — used to build the Weighted Share Values workbook sheet key for fund concluded values. */
  companyId?: string | number;
  /**
   * Weighted share values — used for **fund** rows and fund concluded amounts only.
   * Security rows under "Share Value" come from {@link CreateRangeSensitivityConfigurationParams.securities}, not from listing `share_values` here.
   */
  weightedShareValues: AllocationWeightedShareValue[];
  /** Primary cap table securities (same list as Equity Allocation). */
  securities?: AllocationScenarioDetailSharesSecurityModel[];
}

const afterCellChanged = ((changes: Parameters<AfterCellChanged>[0]) => changes) as AfterCellChanged;

const createRangeSensitivityConfiguration = (
  params: CreateRangeSensitivityConfigurationParams,
): RangeSensitivitySpreadsheetConfig => {
  const {
    workbookKey,
    pctSheetKey,
    allocation,
    companyName,
    companyId,
    weightedShareValues,
    securities,
  } = params;

  const rowConfig = customRowConfig({ companyName, securities, weightedShareValues });
  const columns = createColumns({ allocation, weightedShareValues });

  const parser: RangeSensitivitySpreadsheetConfig['parser'] = parserParams =>
    customParser({
      columns: parserParams.columns as Columns,
      rowConfig: parserParams.rowConfig as Rows,
      tableData: parserParams.tableData as
        | { equityAllocationSheetKey?: string; companyId?: string | number; pctSheetKey?: string }
        | undefined,
    }) as ConfigurationCellMap<object, object, object, object>;

  // SpreadsheetConfigConstructorParams extends ScalarSpreadsheetConfigDataInterface, so the constructor
  // parameter type incorrectly requires class methods (evaluate, reset, …) that the real constructor
  // never expects in the options object. ConstructorParameters<typeof SpreadsheetConfig<…>> extracts
  // the constructor's formal parameter list as a tuple; `[0]` is the first (and only) parameter —the
  // options bag— so we can assert our literal through `unknown` to that type without duplicating stubs.
  return new SpreadsheetConfig({
    name: workbookKey,
    tableData: {
      equityAllocationSheetKey: generateEquityAllocationKey(),
      companyId,
      pctSheetKey,
    },
    afterCellChanged,
    ignoreCellExpressionErrors: false,
    columns,
    rowConfig,
    parser,
    currencyFormatter: true,
    unitsFormatter: true,
    showTitlesColumn: true,
    tableTerms: {
      columnsHeaders: RANGE_SENSITIVITY_COLUMN_HEADERS,
    },
  } as unknown as ConstructorParameters<typeof SpreadsheetConfig<object, object, object>>[0]) as RangeSensitivitySpreadsheetConfig;
};

export default createRangeSensitivityConfiguration;
